import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../../services/authService';
import { validateForceChangePassword, hasErrors, FieldErrors } from '../../utils/validationUtils';
import Footer from '../../components/Footer';

const ForceChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const role = typeof window !== 'undefined' ? localStorage.getItem('role') : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setSubmitting(true);
    const validationErrors = validateForceChangePassword({ oldPassword, newPassword, confirmPassword });
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      setSubmitting(false);
      return;
    }
    setErrors({});
    try {
      await changePassword({ oldPassword, newPassword });
      // Clear flag & redirect
      localStorage.removeItem('forcePasswordChange');
      if (role === 'Admin') navigate('/admin/dashboard');
      else navigate('/artist/dashboard');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Password change failed';
      setApiError(String(msg));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background pb-16">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-lg p-8 border border-secondary/20">
          <h1 className="text-2xl font-semibold text-primary mb-2 text-center">Update Your Password</h1>
          <p className="text-sm text-secondary text-center mb-6">For security reasons, please set a new password before continuing.</p>
          {apiError && <div className="bg-red-100 text-red-600 p-2 rounded mb-4 text-sm">{apiError}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Current Password</label>
              <input
                type="password"
                value={oldPassword}
                onChange={e => setOldPassword(e.target.value)}
                className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                placeholder="Enter current password"
                required
              />
              {errors.oldPassword && <p className="text-xs text-red-600 mt-1">{errors.oldPassword}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                placeholder="Choose a strong password"
                required
              />
              {errors.newPassword && <p className="text-xs text-red-600 mt-1">{errors.newPassword}</p>}
              <p className="text-[11px] text-secondary/70 mt-1">Min 8 chars, include 3 of: uppercase, lowercase, digit, symbol.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full border border-secondary/30 rounded-xl px-3 py-2 bg-background focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                placeholder="Repeat new password"
                required
              />
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-primary py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForceChangePassword;
export {};
